import moment from 'moment';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import TimeFormatter from 'utils/TimeFormatter';
import { type TimeFormat } from 'vd-constants';

interface FormattedTimeProps {
  date: Date | number;
  format?: TimeFormat;
  zeroPrefix?: boolean;
  withDate?: boolean | 'only' | 'date-first';
  hideDateTimezone?: boolean;
  seconds?: boolean;
  timezoneId?: string;
  dateFormatString?: string;
}

/**
 * Renders a formatted date, based on the user's 12/24 hour prefernces.
 */
const FormattedTime: React.FC<FormattedTimeProps> = (props) => {
  const time = useFormattedTime(props.date, {
    seconds: props.seconds,
    zeroPrefix: props.zeroPrefix,
    withDate: props.withDate,
    format: props.format,
    timezoneId: props.timezoneId,
    hideDateTimezone: props.hideDateTimezone,
    dateFormatString: props.dateFormatString,
  });
  if (time === undefined) {
    return null;
  }
  return <>{time}</>;
};

export default React.memo(FormattedTime);

/**
 * Renders a formatted date, based on the user's 12/24 hour preferences.
 */
export function useFormattedTime(
  date: Date | number | undefined,
  options: {
    format?: TimeFormat;
    zeroPrefix?: boolean;
    withDate?: boolean | 'only' | 'date-first';
    seconds?: boolean;
    timezoneId?: string;
    hideDateTimezone?: boolean;
    dateFormatString?: string;
  } = {},
) {
  const { withDate, timezoneId, hideDateTimezone } = options;

  const locale = useLocale();

  const timeFormatString = TimeFormatter.getString({
    zeroPrefix: options.zeroPrefix,
    seconds: options.seconds,
    timeFormat: options.format,
    showTimezone: !!timezoneId,
  });

  let dateFormatString =
    timezoneId && !hideDateTimezone ? locale.date.monthAndDayAndTimeZone : locale.date.monthAndDay;

  if (options.dateFormatString) {
    dateFormatString = options.dateFormatString;
  }

  let formatString;
  if (withDate === 'only') {
    formatString = dateFormatString;
  } else if (withDate === 'date-first') {
    formatString = `${dateFormatString} - ${timeFormatString}`;
  } else if (withDate) {
    formatString = `${timeFormatString} - ${dateFormatString}`;
  } else {
    formatString = timeFormatString;
  }

  if (date === undefined) {
    return undefined;
  } else if (timezoneId) {
    return moment(date).tz(timezoneId).format(formatString);
  }
  return moment(date).format(formatString);
}
